import React from 'react';
import { css, styled } from 'styled-components';
import { ChevronIcon as ArrowDown } from 'src/components/widgets/icons/scene';
import { Icon } from 'src/components/widgets/icon';
import { createContext, useContext, useEffect, useRef } from 'react';
import { useSectionOpen } from './propertiesPanelSectionMemory';
import { useIsSceneLook } from './propertiesPanelLook';
import { panel } from './panelTheme';
import { PropertiesPanelPropertyContainer } from './propertiesPanelProperty';

export const PropertiesPanelAccordionContainer = styled.div`
    font-family: ${(props) => props.theme.app.font};
`;

export const PropertiesPanelAccordionHeader = styled.div`
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    gap: 8px;
    align-items: center;
    display: flex;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    padding: 6px 0px;

    ${Icon} {
        margin-left: auto;
        color: #445a64;
    }
`;

export const PropertiesPanelAccordionContent = styled.div<{ $isOpen: boolean }>`
    max-height: ${(props) => (props.$isOpen ? '3000px' : '0')};
    visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
    transition: max-height 300ms;
    overflow: hidden;
`;

// LA GRAMMATICA DELL'INSPECTOR (prototipo V3, AGGIORNAMENTI §2; utente, 2026-10-20). Un gruppo è una di tre cose,
// e lo decide dove sta, non chi lo scrive:
//
// - AL PRIMO LIVELLO È UNA SEZIONE: l'etichetta in maiuscoletto sta fuori, sopra, e il contenuto è raccolto
//   in una card #F9FAFB (utente, 2026-10-20: come le card dei materiali nel prototipo, si legge meglio dei campi
//   posati sul fondo). Non si chiude: una colonna di sezioni si legge scorrendo, e otto frecce da aprire erano
//   otto gesti per vedere quello che c'è. Una sezione con l'interruttore, spenta, mostra solo l'etichetta.
//   Una sezione fatta solo di accordion, solo di canali o solo di slot non ha la card (prototipo; utente,
//   2026-10-20): l'accordion, il canale e lo slot hanno già la loro cornice, e dentro una card sarebbero una card
//   di card. I canali, che sono card, stanno a quattro pixel l'uno dall'altro.
// - DENTRO UNA SEZIONE È UN ACCORDION: una card con la freccia, che aperta mostra il contenuto su un tono
//   più incassato, quattro pixel dentro. Lì chiudere serve, perché è un dettaglio dentro un argomento.
//
// LA FRECCIA STA A SINISTRA, prima del nome. A destra ci va ciò che comanda il gruppo — un interruttore —
// e le due cose non possono contendersi lo stesso angolo.
const AccordionDepthContext = createContext(0);

const caps = css`
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

// IL PASSO FRA DUE RIGHE LO TIENE IL CORPO, UNA VOLTA SOLA. Scritto sulle righe erano tanti margini quante
// specie di riga — un campo, un interruttore, un canale — e bastava aggiungerne una perché la colonna
// cambiasse ritmo a metà.
//
// LA COLONNA DEI NOMI SE LA MISURA IL CORPO, sulle proprie etichette, con un tetto del 50%: se proprio si
// deve cedere, cede il nome, perché un nome su due righe si legge ancora e un controllo schiacciato no.
// Le righe che NON sono figlie dirette — dentro un blocco o un accordion annidato — fanno la loro misura.
//
// IL VALORE HA LA SUA MISURA, IL NOME IL RESTO (prototipo, .fld e --ctl-wide = 174px; utente 2026-10-21): campi,
// slider e tendine sono larghi al più 174px e stanno a destra, allineati fra loro; in un Inspector stretto cedono
// loro prima del nome. Le terne (X · Y · Z) restano fuori: tre campi in 174px non ci stanno.
const bodyGrid = css`
    display: grid;
    grid-template-columns: minmax(64px, 1fr) minmax(0, 174px);
    align-content: start;
    column-gap: 10px;
    row-gap: 8px;

    /* Ciò che non è una riga di parametro — un accordion, un blocco, un'immagine — prende tutte e due le colonne. */
    > * {
        grid-column: 1 / -1;
    }

    /* IL PASSO FRA LE COLONNE LO DETTA IL CORPO, non la riga: due righe con due gronde diverse farebbero
       partire i loro controlli da due x diverse. LE RIGHE \`lone\` NON ENTRANO NELLA MISURA: il loro nome si
       prende lo spazio che il comando non usa. */
    > ${PropertiesPanelPropertyContainer}:not([data-lone]):not([data-narrow]) {
        display: grid;
        grid-template-columns: subgrid;
        column-gap: 10px;
    }
`;

const SceneSection = styled.section`
    padding: 12px 0 4px;

    &:first-child {
        padding-top: 4px;
    }

    /* Un comando che segue le sezioni — «Reset all camera settings» — non si attacca alla card di sopra. */
    & + :not(section) {
        margin-top: 12px;
    }
`;

// IL BERSAGLIO DELL'INTERRUTTORE STA ACCANTO AL NOME, non dentro una maniglia: un interruttore annidato in
// una testata cliccabile la aprirebbe ogni volta che lo si spegne.
const SceneSectionHead = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 22px;
`;

const SceneSectionLabel = styled.span`
    flex: 1;
    min-width: 0;
    color: var(--editor-faint, #676a6e);
    font-size: 10px;
    ${caps}
`;

const SceneSub = styled.div`
    padding: 4px;
    border-radius: 10px;
    background: var(--editor-panel, ${panel.surface});
`;

const SceneSectionBody = styled.div<{ $flat?: boolean }>`
    ${bodyGrid}
    margin-top: 4px;
    padding: 8px 8px 10px;
    border-radius: 10px;
    background: var(--editor-panel, ${panel.surface});

    /* Solo slot: c'è uno slot e nessun altro comando — un campo, un interruttore, un bottone fuori dagli slot. */
    &:not(:has(> :not(${SceneSub}))),
    &:not(:has(> :not([data-channel]))),
    &:has([data-slot]):not(:has(input, textarea, [role='switch'], [role='slider'], button:not([data-slot] button))) {
        padding: 0;
        background: transparent;
    }

    &:not(:has(> :not([data-channel]))) {
        row-gap: 4px;
    }

    ${(props) =>
        props.$flat &&
        css`
            && {
                padding: 4px 0 0;
                background: transparent;
            }
        `}
`;

const SceneSubHeadRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SceneSubHead = styled.button<{ $isOpen: boolean; $fixed?: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    min-height: 22px;
    padding: 0 4px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--editor-faint, ${panel.inkMuted});
    font-family: inherit;
    font-size: 11px;
    ${caps}
    text-align: left;
    cursor: ${(props) => (props.$fixed ? 'default' : 'pointer')};

    ${Icon} {
        flex: 0 0 auto;
        width: 12px;
        color: var(--editor-faint, ${panel.inkMuted});
        transform: rotate(${(props) => (props.$isOpen ? '0deg' : '-90deg')});
        transition: transform 130ms ease;
    }

    &:hover {
        background: ${(props) => (props.$fixed ? 'transparent' : 'rgb(0 0 0 / 3%)')};
    }
`;

const SceneSubBody = styled.div`
    ${bodyGrid}
    margin-top: 4px;
    padding: 6px;
    border-radius: 6px;
    background: var(--editor-sunken, #e9ebed);
`;

export const PropertiesPanelAccordion = (props: {
    defaultIsOpen?: boolean;
    /**
     * IL NOME CON CUI LA SEZIONE SI RICORDA CHIUSA (`propertiesPanelSectionMemory`): stabile e non tradotto
     * — «materials», «size». Senza, la sezione dimentica come prima, appena esce dallo schermo.
     */
    memoryId?: string;
    /**
     * APERTA COMUNQUE, finché vale: dentro c'è un errore da correggere, e una sezione chiusa da una scelta
     * di ieri non deve nasconderlo. Quando l'errore se ne va, torna com'era stata lasciata.
     */
    forceOpen?: boolean;
    header: React.ReactNode;
    /** Ciò che comanda il gruppo intero: un interruttore, un segmentato. Solo nel vestito della scena. */
    trailing?: React.ReactNode;
    /**
     * Perché quello che c'è qui dentro si comporta in modo inatteso. Sta FUORI dalla maniglia,
     * come l'interruttore: chiedere una spiegazione non è chiedere di chiudere il gruppo.
     */
    hint?: React.ReactNode;
    /**
     * QUANDO IL GRUPPO SI ACCENDE, SI APRE. Si accende una funzione per usarla, e trovarla
     * ancora chiusa vorrebbe dire due gesti per uno. Spegnendola invece **non si richiude**:
     * chi spegne sta guardando quello che spegne, e un pannello che si chiude sotto le dita
     * toglie di vista la cosa appena toccata.
     *
     * Lo dice chi monta l'interruttore, perché è lui che sa quale stato governa il gruppo: da
     * qui dentro `trailing` è un nodo React qualunque, e indovinarne il valore vorrebbe dire
     * leggere dentro un pezzo di interfaccia che non ci appartiene.
     */
    openWhen?: boolean;
    /**
     * UNA CARD CHE NON SI APRE, perché non ha dentro niente da aprire: quello che dice sta tutto
     * nella testata — il tipo di una luce, una parola e basta. La freccia sparisce e la testata
     * smette di essere un pulsante: una maniglia che non porta da nessuna parte è una promessa
     * rotta, e un pulsante che non fa niente lo è due volte.
     */
    fixed?: boolean;
    /** SENZA CARD (prototipo; utente, 2026-10-20): Transform poggia direttamente sull'Inspector, le altre sezioni no. */
    flat?: boolean;
    children: React.ReactNode;
}) => {
    const [rememberedOpen, setIsOpen] = useSectionOpen(props.memoryId, props.defaultIsOpen ?? true);
    const isOpen = rememberedOpen || Boolean(props.forceOpen);
    const scene = useIsSceneLook();
    const depth = useContext(AccordionDepthContext);

    // Si apre al PASSAGGIO da spento ad acceso, non finché è acceso: altrimenti un gruppo già
    // acceso non si potrebbe più chiudere, si riaprirebbe al ridisegno successivo.
    const wasOn = useRef(props.openWhen);

    useEffect(() => {
        if (props.openWhen && !wasOn.current) setIsOpen(true);
        wasOn.current = props.openWhen;
    }, [props.openWhen]);

    if (scene) {
        // SPENTA, UNA SEZIONE MOSTRA SOLO L'ETICHETTA: lo dice chi monta l'interruttore, con `openWhen`.
        const switchedOff = props.openWhen === false;

        if (depth === 0) {
            return (
                <SceneSection>
                    <SceneSectionHead>
                        <SceneSectionLabel>{props.header}</SceneSectionLabel>
                        {props.hint}
                        {props.trailing}
                    </SceneSectionHead>
                    {!props.fixed && !switchedOff && (
                        <AccordionDepthContext.Provider value={depth + 1}>
                            <SceneSectionBody $flat={props.flat}>{props.children}</SceneSectionBody>
                        </AccordionDepthContext.Provider>
                    )}
                </SceneSection>
            );
        }

        return (
            <SceneSub>
                <SceneSubHeadRow>
                    {props.fixed ? (
                        <SceneSubHead as='span' $isOpen={false} $fixed>
                            {props.header}
                        </SceneSubHead>
                    ) : (
                        <SceneSubHead type='button' $isOpen={isOpen} aria-expanded={isOpen} onClick={() => setIsOpen((prev) => !prev)}>
                            <Icon>
                                <ArrowDown />
                            </Icon>
                            {props.header}
                        </SceneSubHead>
                    )}
                    {props.hint}
                    {props.trailing}
                </SceneSubHeadRow>
                {!props.fixed && isOpen && (
                    <AccordionDepthContext.Provider value={depth + 1}>
                        <SceneSubBody>{props.children}</SceneSubBody>
                    </AccordionDepthContext.Provider>
                )}
            </SceneSub>
        );
    }

    return (
        <PropertiesPanelAccordionContainer>
            <PropertiesPanelAccordionHeader onClick={() => setIsOpen((prev) => !prev)}>
                {props.header}
                {props.hint}
                <Icon>
                    <ArrowDown />
                </Icon>
            </PropertiesPanelAccordionHeader>
            <PropertiesPanelAccordionContent $isOpen={isOpen}>{props.children}</PropertiesPanelAccordionContent>
        </PropertiesPanelAccordionContainer>
    );
};
